/** How Stripe's lowercase brand ids read to a person. */
const BRAND_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

/**
 * A card brand as a person writes it — "Visa", not "visa" or "VISA".
 *
 * Shared rather than duplicated per surface because the same card is named in
 * more than one place at once: a refund dialog says where the money goes back
 * to while the payment methods list says what it was taken from, and the two
 * reading differently makes them look like different cards.
 *
 * An unmapped brand is capitalised rather than dropped: a card Stripe adds
 * support for should still read as a name, not vanish.
 */
export function describePaymentMethodBrand(brand: string | null | undefined): string | undefined {
  const normalizedBrand = brand?.trim().toLowerCase();
  if (!normalizedBrand) {
    return undefined;
  }
  return BRAND_DISPLAY_NAMES[normalizedBrand] ?? normalizedBrand.charAt(0).toUpperCase() + normalizedBrand.slice(1);
}

/**
 * "Visa ending 1234" — the card a payment was taken from, or goes back to.
 *
 * Absent when the last four are not known, rather than guessed: a label that
 * names the wrong card is worse than a row that is left out, because the
 * label exists to tell someone which of their cards is meant.
 */
export function describePaymentMethod(
  brand: string | null | undefined,
  last4: string | null | undefined,
): string | undefined {
  if (!last4) {
    return undefined;
  }
  return `${describePaymentMethodBrand(brand) ?? 'Card'} ending ${last4}`;
}
